/**
 * VoiceOverlay - Modal/sheet for voice mode.
 *
 * Mobile: Full-height bottom sheet (slides up)
 * Desktop: Floating card near voice button
 *
 * Integrates with existing audio hooks for voice capture and playback.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { X, Settings, Radio, Phone } from 'lucide-react'
import type { VoiceState } from './VoiceButton'
import { VoiceVisualizer } from './VoiceVisualizer'
import { VoiceControls } from './VoiceControls'
import { useAuthStore } from '@/store/authStore'
import { useLiveStore } from '@/store/liveStore'
import { useAudioCapture } from '@/hooks/useAudioCapture'
import { useAudioPlayback } from '@/hooks/useAudioPlayback'
import { createLiveConnection, type LiveConnection } from '@/api/live'

// Voice options available from Gemini
const VOICES = [
  { id: 'Aoede', name: 'Aoede', description: 'Warm and friendly' },
  { id: 'Puck', name: 'Puck', description: 'Playful and energetic' },
  { id: 'Kore', name: 'Kore', description: 'Calm and professional' },
  { id: 'Charon', name: 'Charon', description: 'Deep and authoritative' },
  { id: 'Fenrir', name: 'Fenrir', description: 'Bold and confident' },
]

interface VoiceOverlayProps {
  isOpen: boolean
  onClose: () => void
  onTranscript?: (text: string, role: 'user' | 'assistant', isFinal: boolean) => void
}

export function VoiceOverlay({ isOpen, onClose, onTranscript }: VoiceOverlayProps) {
  const token = useAuthStore((state) => state.token)
  const {
    isConnected,
    isRecording,
    isMuted,
    selectedVoice,
    status,
    setConnected,
    setRecording,
    setMuted,
    setVoice,
    addTranscript,
    updateTranscript,
    addTool,
    updateTool,
    setStatus,
  } = useLiveStore()

  const [showSettings, setShowSettings] = useState(false)
  const [interactionMode, setInteractionMode] = useState<'push-to-talk' | 'hands-free'>('push-to-talk')
  const [isAiSpeaking, setIsAiSpeaking] = useState(false)
  // VoiceState for potential future UI enhancements (e.g., status badge)
  const [, setVoiceState] = useState<VoiceState>('idle')

  const connectionRef = useRef<LiveConnection | null>(null)

  // Audio playback hook
  const { play, interrupt } = useAudioPlayback({
    onPlaybackStart: () => {
      setIsAiSpeaking(true)
      setVoiceState('speaking')
    },
    onPlaybackStop: () => {
      setIsAiSpeaking(false)
      if (isConnected) {
        setVoiceState('listening')
      }
    },
  })

  // Audio capture hook
  const audioCapture = useAudioCapture({
    onChunk: (pcm) => {
      connectionRef.current?.sendAudio(pcm)
    },
    onStart: () => {
      setRecording(true)
      connectionRef.current?.sendActivity('speaking')
      interrupt()
    },
    onStop: () => {
      setRecording(false)
      connectionRef.current?.sendActivity('listening')
    },
    onError: (error) => {
      console.error('Audio capture error:', error)
      setStatus(`Microphone error: ${error.message}`)
      setVoiceState('error')
    },
  })

  // Update voice state based on connection and activity
  useEffect(() => {
    if (!isConnected) {
      setVoiceState('idle')
    } else if (isRecording) {
      setVoiceState('listening')
    } else if (isAiSpeaking) {
      setVoiceState('speaking')
    } else {
      setVoiceState('listening')
    }
  }, [isConnected, isRecording, isAiSpeaking])

  // Connect to live session
  const connect = useCallback(async () => {
    if (!token) return

    setStatus('Connecting...')
    setVoiceState('connecting')

    const connection = createLiveConnection({
      token,
      voice: selectedVoice,
    })

    connection.setCallbacks({
      onReady: (_sessionId, voice) => {
        setConnected(true)
        setStatus(`Connected (voice: ${voice})`)
        setVoiceState('listening')
      },
      onAudio: (audioB64) => {
        play(audioB64)
      },
      onTranscript: (text, role, isFinal) => {
        const entry = {
          id: crypto.randomUUID(),
          role: role === 'user' ? ('user' as const) : ('assistant' as const),
          text,
          isFinal,
          timestamp: new Date(),
        }
        if (isFinal) {
          addTranscript(entry)
          onTranscript?.(text, entry.role, true)
        } else {
          updateTranscript(entry)
          onTranscript?.(text, entry.role, false)
        }
      },
      onToolStart: (name, args) => {
        addTool({ name, args, status: 'running' })
        setVoiceState('processing')
      },
      onToolResult: (name, result, success, durationMs) => {
        updateTool(name, {
          result,
          status: success ? 'success' : 'error',
          durationMs,
        })
      },
      onStatus: (message) => {
        setStatus(message)
      },
      onError: (error) => {
        setStatus(`Error: ${error}`)
        setVoiceState('error')
      },
      onEnd: () => {
        setConnected(false)
        setStatus('Session ended')
        setVoiceState('idle')
      },
      onDisconnected: () => {
        setConnected(false)
        setStatus('Disconnected')
        setVoiceState('idle')
      },
    })

    connectionRef.current = connection

    try {
      await connection.connect()
    } catch (error) {
      setStatus('Connection failed')
      setVoiceState('error')
      console.error('Connection error:', error)
    }
  }, [token, selectedVoice, setConnected, setStatus, play, addTranscript, updateTranscript, addTool, updateTool, onTranscript])

  // Disconnect
  const disconnect = useCallback(() => {
    if (audioCapture.isRecording) {
      audioCapture.stop()
    }
    interrupt()
    connectionRef.current?.disconnect()
    connectionRef.current = null
    setConnected(false)
    setStatus('Disconnected')
    setVoiceState('idle')
  }, [audioCapture, interrupt, setConnected, setStatus])

  // Toggle mute
  const toggleMute = useCallback(() => {
    setMuted(!isMuted)
  }, [isMuted, setMuted])

  // Handle push-to-talk
  const handlePushToTalk = useCallback(async (pressed: boolean) => {
    if (!isConnected) return

    if (pressed) {
      await audioCapture.start()
    } else {
      audioCapture.stop()
    }
  }, [isConnected, audioCapture])

  // Keyboard handler for push-to-talk (spacebar)
  useEffect(() => {
    if (!isOpen || interactionMode !== 'push-to-talk' || !isConnected) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat && !isRecording) {
        e.preventDefault()
        handlePushToTalk(true)
      }
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault()
        handlePushToTalk(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [isOpen, interactionMode, isConnected, isRecording, handlePushToTalk])

  // Cleanup on close
  useEffect(() => {
    if (!isOpen && isConnected) {
      disconnect()
    }
  }, [isOpen, isConnected, disconnect])

  // Handle close
  const handleClose = useCallback(() => {
    disconnect()
    onClose()
  }, [disconnect, onClose])

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Overlay panel */}
      <div
        className="fixed z-50 bg-surface border border-surface-border rounded-t-2xl md:rounded-2xl shadow-xl animate-in slide-in-from-bottom duration-300
          inset-x-0 bottom-0 md:bottom-auto md:right-4 md:left-auto md:top-1/2 md:-translate-y-1/2
          w-full md:w-96 max-h-[80vh] md:max-h-[600px] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-border">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-gray-500'}`} />
            <h2 className="font-semibold">Voice Mode</h2>
          </div>

          <div className="flex items-center gap-2">
            {/* Mode toggle */}
            <button
              onClick={() => setInteractionMode(interactionMode === 'push-to-talk' ? 'hands-free' : 'push-to-talk')}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                interactionMode === 'hands-free'
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-surface border border-surface-border text-text-secondary'
              }`}
            >
              <Radio className="w-4 h-4 inline mr-1" />
              {interactionMode === 'hands-free' ? 'Hands-free' : 'PTT'}
            </button>

            {/* Settings */}
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 rounded-lg bg-surface border border-surface-border hover:bg-surface-hover transition-colors"
            >
              <Settings className="w-5 h-5 text-text-secondary" />
            </button>

            {/* Close */}
            <button
              onClick={handleClose}
              className="p-2 rounded-lg hover:bg-surface-hover transition-colors"
            >
              <X className="w-5 h-5 text-text-muted" />
            </button>
          </div>
        </div>

        {/* Settings panel */}
        {showSettings && (
          <div className="px-4 py-3 border-b border-surface-border bg-surface/30 animate-in slide-in-from-top duration-200">
            <div className="flex items-center gap-4">
              <label className="text-sm text-text-secondary">Voice:</label>
              <select
                value={selectedVoice}
                onChange={(e) => setVoice(e.target.value)}
                disabled={isConnected}
                className="input py-1 px-2 text-sm flex-1"
              >
                {VOICES.map((voice) => (
                  <option key={voice.id} value={voice.id}>
                    {voice.name} - {voice.description}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Main content */}
        <div className="p-6">
          {/* Status */}
          {status && (
            <div className="text-center text-sm text-text-muted mb-4">
              {status}
            </div>
          )}

          {/* Voice visualizers */}
          <div className="flex justify-center gap-12 mb-6">
            <div className="text-center">
              <VoiceVisualizer isActive={isRecording} role="user" />
              <p className="text-xs text-text-muted mt-1">You</p>
            </div>
            <div className="text-center">
              <VoiceVisualizer isActive={isAiSpeaking} role="ai" />
              <p className="text-xs text-text-muted mt-1">Neuro-HA</p>
            </div>
          </div>

          {/* Main button or controls */}
          {!isConnected ? (
            <div className="flex justify-center">
              <button
                onClick={connect}
                className="p-5 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 transition-colors shadow-lg shadow-emerald-500/30"
              >
                <Phone className="w-8 h-8" />
              </button>
            </div>
          ) : (
            <VoiceControls
              isRecording={isRecording}
              isMuted={isMuted}
              onPushToTalk={handlePushToTalk}
              onMuteToggle={toggleMute}
              onEndCall={disconnect}
              mode={interactionMode}
            />
          )}

          {/* Keyboard hint */}
          {isConnected && interactionMode === 'push-to-talk' && (
            <p className="text-center text-xs text-text-muted mt-4">
              Hold <kbd className="px-1.5 py-0.5 rounded bg-surface border border-surface-border">Space</kbd> to talk
            </p>
          )}
        </div>
      </div>
    </>
  )
}

export default VoiceOverlay
